import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Text, Flex } from '@chakra-ui/react';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';
import {
  Input,
  FormFeedback,
  IButton,
  Button,
  Modal
} from '@morressier/alembic';
import { notesContentSchema } from '@publishing-engine/domain/validation';
import { FieldLabel } from 'src/components/Field';
import TaggingEditor from './MentionsPlugin';
import { searchJournals, searchUsers } from 'src/api/queries';
import { debounce } from 'lodash';
import { SELECTED_ITEM_QUERY_KEY } from 'src/constants';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import {
  ISubmissionFile,
  TaggableEntity
} from '@publishing-engine/domain/models';
import { toSentenceCasePreserveAcronyms } from 'src/utils/string';

type INoteFormPayload = {
  title: string;
  note: string;
  mentions: TaggableEntity[];
  attachments?: ISubmissionFile[];
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onEditSave: (data: INoteFormPayload) => Promise<any>;
  note: ISubmissionNotesListItem;
  organizationId: string;
  actionButtonProps?: Partial<IButton>;
  submissionContextId: string;
}

const EditNoteModal: React.FC<Props> = ({
  isOpen,
  onClose,
  note,
  onEditSave,
  organizationId,
  submissionContextId
}) => {
  const { submissionId: paramId = '' } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const selectedItem = searchParams.get(SELECTED_ITEM_QUERY_KEY);
  const submissionId = selectedItem || paramId;
  // This is used to force the entire form to rerender on submit
  const [formKey, setFormKey] = useState(0);
  // This is used to force a re-render of the editor when the note is updated
  const [editorKey, setEditorKey] = useState(0);
  const isResettingRef = useRef(false);
  const [userQuery, setUserQuery] = useState<string>('');
  const [journalQuery, setJournalQuery] = useState<string>('');

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-search', userQuery, { organizationId }],
    queryFn: () => searchUsers(userQuery, { organizationId })
  });

  const { data: journals, isLoading: isLoadingJournals } = useQuery({
    queryKey: ['journals-search', journalQuery, { organizationId }],
    queryFn: () => searchJournals(journalQuery, { organizationId })
  });

  const mappedUsers = users?.collection.map(user => ({
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    type: 'user' as const
  }));

  const mappedJournals = journals?.collection.map(journal => ({
    id: journal.id,
    name: journal.data.name,
    email: journal.config.email ?? '',
    type: 'journal' as const
  }));

  const method = useForm<INoteFormPayload>({
    resolver: zodResolver(notesContentSchema),
    mode: 'onSubmit',
    defaultValues: {
      title: '',
      note: '',
      mentions: [],
      attachments: []
    }
  });
  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    watch,
    reset
  } = method;

  useEffect(() => {
    if (isOpen && note) {
      reset({
        title: note.title,
        note: note.content,
        mentions: note.mentions || [],
        attachments: note.attachments || []
      });

      setEditorKey(prev => prev + 1);
      setFormKey(prev => prev + 1);
    }
  }, [isOpen, note, reset]);

  const handleEditNote = async (_values: INoteFormPayload) => {
    const success = await onEditSave(_values);
    if (success) {
      onClose();
    }
  };

  const onCancel = () => {
    isResettingRef.current = true;
    reset({
      title: note.title,
      note: note.content,
      mentions: note.mentions || [],
      attachments: note.attachments || []
    });
    setEditorKey(prev => prev + 1);
    setFormKey(prev => prev + 1);
    setTimeout(() => {
      isResettingRef.current = false;
    }, 50);

    onClose();
  };

  const handleQueryChange = useCallback(
    debounce((query: string, key: '@' | '#') => {
      if (key === '@') return setUserQuery(query);
      return setJournalQuery(query);
    }, 300),
    [setUserQuery, setJournalQuery]
  );
  return (
    <Modal isOpen={isOpen} onClose={onCancel} width="700px">
      <Flex flexDirection="column" padding={4}>
        <Text textStyle="headingGroup">Edit Note</Text>
      </Flex>
      <Flex flexDirection="column" padding={4}>
        <FormProvider {...method}>
          <form onSubmit={handleSubmit(handleEditNote)}>
            <Flex
              key={formKey}
              justifyContent="start"
              gap={4}
              flexDirection="column"
            >
              <Flex flexDirection="column" flex="1" gap={2}>
                <FieldLabel label="Title" subText="" isRequired={true} />
                <Input
                  {...register('title')}
                  type="text"
                  placeholder="Enter the title"
                  maxLength={100}
                />
                <Flex flexDirection="column" width="100%">
                  <Flex justifyContent="space-between" width="100%">
                    <Text textStyle="textContext">
                      Character limit for the title is 100 characters.
                    </Text>
                    <Text textStyle="textContext">
                      {(watch('title') || '').length} / 100
                    </Text>
                  </Flex>
                  <Flex mt={1}>
                    {errors.title && (
                      <FormFeedback variant="error">
                        {toSentenceCasePreserveAcronyms(errors.title?.message ?? '')}
                      </FormFeedback>
                    )}
                  </Flex>
                </Flex>
              </Flex>
              <Flex flexDirection="column" flex="1" gap={2}>
                <FieldLabel
                  label="Note"
                  subText="Add notes about this submission and collaborate by tagging team
                        members with @ or journal staff with #."
                  isRequired={false}
                  subTextProps={{ textStyle: 'textContext' }}
                />
                <TaggingEditor
                  defaultValue={note.content}
                  key={editorKey}
                  entities={{
                    isLoading: isLoadingUsers || isLoadingJournals,
                    at: mappedUsers || [],
                    hashtag: mappedJournals || []
                  }}
                  setQuery={handleQueryChange}
                  onContentChange={({ note, mentions }) => {
                    if (isResettingRef.current) {
                      return;
                    }
                    setValue('note', note);
                    setValue('mentions', [...mentions]);
                  }}
                />
                <Flex justifyContent="space-between">
                  <Text textStyle="textContext">
                    Character limit for note is 1000 characters.
                  </Text>

                  <Text textStyle="textContext">
                    {(watch('note') || '').length} / 1000
                  </Text>
                </Flex>
                {(errors.note || errors.mentions) && (
                  <FormFeedback variant="error">
                    {errors.note?.message || errors.mentions?.message}
                  </FormFeedback>
                )}
              </Flex>

              <FormFieldMultipleFileUpload
                submissionContextId={submissionContextId}
                submissionId={submissionId}
                config={{
                  name: 'attachments',
                  params: {
                    validations: {
                      kind: 'fileArray',
                      maxSizeMB: 25,
                      maxItems: 15,
                      allowedFileFormats: {
                        'image/jpeg': ['.jpg'],
                        'image/png': ['.png'],
                        'application/pdf': ['.pdf'],
                        'application/msword': ['.doc'],
                        'application/vnd.openxmlformats': ['.docx'],
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                          ['.xlsx']
                      },
                      required: false
                    },
                    labels: {
                      main: 'Attach File(s)',
                      subtitle: ''
                    }
                  },
                  caseDataFields: {
                    main: 'attachments'
                  }
                }}
                helperText="PDF, DOCX, XLSX, PNG and JPG"
              />
              <Flex
                gap={2}
                justifyContent="flex-end"
                alignItems="center"
                alignSelf="stretch"
              >
                <Button
                  variant="secondary"
                  onClick={onCancel}
                  borderRadius="small"
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" borderRadius="small">
                  Save Changes
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </Modal>
  );
};

export default EditNoteModal;
